import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import Nav from '../components/Nav';
import { supabase } from '../lib/supabase';
import type { Booking, Vendor } from '../lib/database.types';
import { formatDate, formatTime } from '../lib/format';

// Lands here once, immediately after checkout. Pure celebration: confetti,
// the headline, a "see your booking" hand-off. We keep this thin on purpose
// — the actual RSVP / activity feed / cancel UI lives at /bookings/:id.
export default function BookingSuccess() {
  const { id } = useParams();
  const [booking, setBooking] = useState<Booking | null>(null);
  const [vendor, setVendor] = useState<Vendor | null>(null);

  useEffect(() => {
    if (!id) return;
    (async () => {
      const { data: b } = await supabase.from('ga_bookings').select('*').eq('id', id).maybeSingle();
      if (!b) return;
      setBooking(b);
      const { data: v } = await supabase.from('ga_vendors').select('*').eq('id', b.vendor_id).maybeSingle();
      setVendor(v);
    })();
  }, [id]);

  return (
    <>
      <Nav minimal />
      <Confetti />
      <section style={{ paddingTop: 80, paddingBottom: 96, position: 'relative', zIndex: 1 }}>
        <div className="container-narrow" style={{ textAlign: 'center' }}>
          <div className="eyebrow" style={{ marginBottom: 18, animation: 'ga-fade-up 0.6s ease 0.05s both' }}>
            Confirmed
          </div>
          <h1
            className="display"
            style={{ fontSize: 'clamp(48px, 7vw, 84px)', margin: 0, lineHeight: 1.05, animation: 'ga-fade-up 0.7s ease 0.1s both' }}
          >
            You're <span className="serif-italic" style={{ color: 'var(--wine)' }}>in</span>.
          </h1>
          {vendor && booking && (
            <p
              style={{
                color: 'var(--ink-soft)',
                fontSize: 20,
                marginTop: 22,
                maxWidth: 560,
                marginLeft: 'auto',
                marginRight: 'auto',
                animation: 'ga-fade-up 0.7s ease 0.2s both',
              }}
            >
              <strong style={{ color: 'var(--ink)' }}>{vendor.name}</strong> · {formatDate(booking.booking_date)} · {formatTime(booking.booking_time)} for {booking.headcount}.
              We sent a confirmation to your inbox.
            </p>
          )}
          <div
            style={{
              marginTop: 40,
              display: 'flex',
              justifyContent: 'center',
              gap: 12,
              flexWrap: 'wrap',
              animation: 'ga-fade-up 0.8s ease 0.35s both',
            }}
          >
            {id && (
              <Link to={`/bookings/${id}`} className="btn btn-pop" style={{ fontSize: 16 }}>
                Open your booking →
              </Link>
            )}
            <Link to="/dashboard" className="btn btn-ghost">Back to dashboard</Link>
          </div>
        </div>
      </section>

      <style>{`
        @keyframes ga-fade-up {
          from { opacity: 0; transform: translateY(14px); }
          to   { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </>
  );
}

// Lightweight DOM confetti: a handful of absolutely-positioned divs that
// fall + rotate via CSS keyframes. Runs once on mount, cleans up after
// ~3s. No dependency, no canvas.
function Confetti() {
  const [pieces] = useState(() => {
    const palette = ['#7a1e2e', '#c79b69', '#1f3f3a', '#dcd0c1', '#e8b14c', '#9c4f37'];
    return Array.from({ length: 90 }, (_, i) => ({
      id: i,
      left: Math.random() * 100,
      delay: Math.random() * 0.6,
      duration: 2.6 + Math.random() * 1.4,
      drift: (Math.random() - 0.5) * 160,
      rotate: Math.random() * 720,
      size: 6 + Math.random() * 8,
      color: palette[i % palette.length],
      shape: i % 3,
    }));
  });
  const [visible, setVisible] = useState(true);

  useEffect(() => {
    const t = window.setTimeout(() => setVisible(false), 4200);
    return () => window.clearTimeout(t);
  }, []);

  if (!visible) return null;

  return (
    <div
      aria-hidden
      style={{
        position: 'fixed',
        inset: 0,
        pointerEvents: 'none',
        overflow: 'hidden',
        zIndex: 0,
      }}
    >
      {pieces.map((p) => (
        <span
          key={p.id}
          style={{
            position: 'absolute',
            top: -20,
            left: `${p.left}%`,
            width: p.size,
            height: p.size * (p.shape === 1 ? 0.4 : 1),
            background: p.color,
            borderRadius: p.shape === 2 ? '50%' : 2,
            opacity: 0.95,
            animation: `ga-confetti-fall ${p.duration}s ${p.delay}s cubic-bezier(0.2, 0.6, 0.4, 1) forwards`,
            ['--ga-drift' as any]: `${p.drift}px`,
            ['--ga-rotate' as any]: `${p.rotate}deg`,
          }}
        />
      ))}
      <style>{`
        @keyframes ga-confetti-fall {
          0%   { transform: translate3d(0, -20px, 0) rotate(0); opacity: 1; }
          85%  { opacity: 1; }
          100% { transform: translate3d(var(--ga-drift), 110vh, 0) rotate(var(--ga-rotate)); opacity: 0; }
        }
      `}</style>
    </div>
  );
}
